import React, { useState } from 'react';
import { Accordion, AccordionItem, Input, Tooltip, Chip } from "@heroui/react";
import Select from 'react-select';
import Label from "../Label";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import AccordionCheckboxGroup from "./Accordion-Checkbox-Group";
import { ISectorRangeInput, ISectorSelect, ISectorDynamicUIFields, IDynamicSelectItem } from '../../types/types';
import { SectorFilter } from '../../types/classes/SectorFilter';
import { Sector } from '../../types/classes/Sector';
import { SectorField } from '../../types/classes/SectorField';
import { SectorFilterOptions } from '../../types/enums';
import { useAppSelector } from "../../../../core/rootHooks";
import { selectFlagStateListSelector } from "../../filterSelectors";
import LoadingSpinner from '../../../../shared/components/loading-spinner/LoadingSpinner';
import { isEmpty } from 'lodash';
import { AccordionCheckboxGroupLabel } from './Accordion-Checkbox-Group-Label';



const hideNumberSpinners = `
    input[type='number']::-webkit-outer-spin-button,
    input[type='number']::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    input[type='number'] {
        -moz-appearance: textfield;
    }
`;

// Text Component
const SectorRangeInput: React.FC<ISectorRangeInput['props']> = ({ label, name, startHtmlId, minValue = null, endHtmlId, maxValue = null, endContent, characteristicInfo, sector }) => {

    const [initialStartValue, setInitialStartValue] = useState(sector?.getFilterStringValue(startHtmlId) ?? '');
    const [initialEndValue, setInitialEndValue] = useState(sector?.getFilterStringValue(endHtmlId) ?? '');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.id === startHtmlId) {
            setInitialStartValue(e.target.value);
            if (sector.hasFilter(startHtmlId)) {
                if (e.target.value === '' || e.target.value === undefined) {
                    sector.removeFilter(startHtmlId);
                }
                else {
                    sector.setFilterValue(startHtmlId, name, e.target.value);
                }
            }
            else {
                if (e.target.value !== '' && e.target.value !== undefined) {
                    sector.addFilter(new SectorField(startHtmlId, name, e.target.value));
                }
            }
        }
        else if (e.target.id === endHtmlId) {
            setInitialEndValue(e.target.value);
            if (sector.hasFilter(endHtmlId)) {
                if (e.target.value === '' || e.target.value === undefined) {
                    sector.removeFilter(endHtmlId);
                }
                else {
                    sector.setFilterValue(endHtmlId, name, e.target.value);
                }
            }
            else {
                if (e.target.value !== '' && e.target.value !== undefined) {
                    sector.addFilter(new SectorField(endHtmlId, name, e.target.value));
                }
            }
        }
    };

    return (
        <div className="flex flex-row items-center justify-between">
            <Label title={label} textSize="xs" labelClassName="mt-2" />
            <div className="flex items-center">
                <style>{hideNumberSpinners}</style>
                <Input aria-label={startHtmlId} size="sm" className="w-20 mt-2"
                    type="number" min={minValue ?? ''} max={maxValue ?? ''} id={startHtmlId} placeholder={minValue?.toString()}
                    classNames={{
                        inputWrapper: "h-6 min-h-1.5 rounded",

                    }}
                    value={initialStartValue}
                    onChange={(e) => handleChange(e)}
                    endContent={endContent} />
                <Label title="-" textSize="xs" labelClassName="mx-2 mt-2" />
                <Input aria-label={endHtmlId} size="sm" className="w-20 mt-2"
                    type="number" min={minValue ?? ''} max={maxValue ?? ''} id={endHtmlId} placeholder={maxValue?.toString()}
                    classNames={{
                        inputWrapper: "h-6 min-h-1.5 rounded",
                    }}
                    value={initialEndValue}
                    onChange={(e) => handleChange(e)}
                    endContent={endContent} />
                <div className="ml-1 mt-2"><CharacteristicTooltip key={'p-' + startHtmlId} htmlContent={characteristicInfo} htmlId={endHtmlId} /></div>
            </div>
        </div>
    )
};


// Select Component
const SectorSelect: React.FC<ISectorSelect['props']> = ({ label, name, htmlId, options, characteristicInfo, sector }) => {

    const initialValue = sector?.getFilterStringValue(htmlId) ?? '';
    const initialValuesArray = initialValue.split(',').filter(Boolean).map((val: string) => ({ value: val, label: val }));
    const [value, setValue] = useState(initialValuesArray);

    const handleChange = (selectedOptions: string[]) => {
        const newValueArray = selectedOptions ? selectedOptions.map((option: string) => option) : [];
        const newValue = newValueArray.join(',');
        
        setValue(newValue);
        if (sector.hasFilter(htmlId)) {
            if (newValue === '' || newValue === undefined) {
                sector.removeFilter(htmlId);
            }
            else {
                sector.setFilterValue(htmlId, name, newValue);
            }
        }
        else {
            if (newValue !== '' && newValue !== undefined) {
                sector.addFilter(new SectorField(htmlId, name, newValue));
            }
        }
    }

    const selectOptions: IDynamicSelectItem[] = options.map((option: string) => ({ value: option, label: option }));

    const customStyles = {
        control: (provided: any) => ({
            ...provided,
            minHeight: '28px', // Set the minimum height
            maxHeight: '56px', // Set the height
            width: '200px',
            overflow: 'hidden', // Enable vertical scroll
            '&:hover': {
                maxHeight: '112px', // Expand on hover
            },
        }),
        valueContainer: (provided: any) => ({
            ...provided,
            maxHeight: '56px', // Set the height of the contents
            padding: '0px',
            marginBottom: '0.5px',
            // overflow: 'auto', // Enable vertical scroll
            // height: '22px', // Set the height of the contents
            // padding: '0 6px',
            // maxHeight: '24px', // Set the max height to handle overflow
            overflow: 'hidden', // Hide overflow
            // transition: 'max-height 0.2s ease-in-out', // Smooth transition for height change
            '&:hover': {
                maxHeight: '112px', // Expand on hover
                overflowY: 'auto', // Enable vertical scroll
            },
        }),

        indicatorsContainer: (provided: any) => ({
            ...provided,
            height: '24px', // Set the height
        }),
        indicatorSeparator: (provided: any) => ({
            ...provided,
            height: '16px', // Set the height
            marginTop: '6px',
            marginBottom: '6px',
        }),
        dropdownIndicator: (provided: any) => ({
            ...provided,
            padding: '0px',
            marginRight: '1px',
            height: '16px',
            width: '16px',
        }),
        clearIndicator: (provided: any) => ({
            ...provided,
            padding: '0px',
            margin: '0px',
            height: '16px',
            width: '16px',
        }),
    };


    return (
        <div className="flex">
            <Label title={label} textSize="xs" labelClassName="mt-3 w-32 text-nowrap" />
            <Select isClearable={false} id={htmlId} isSearchable isMulti closeMenuOnSelect={false} closeMenuOnScroll={true} className="text-xs mt-2 flex-auto"
                options={selectOptions} defaultValue={value} styles={customStyles} placeholder={""}
                onChange={(selectedOptions) => handleChange(selectedOptions.map((option: IDynamicSelectItem) => option.value))}
            />

            <div className="ml-1 mt-2"><CharacteristicTooltip key={'p-' + htmlId} htmlContent={characteristicInfo} htmlId={htmlId} /></div>
        </div>
    );
};

// Popover Component
const CharacteristicTooltip: React.FC<{ htmlContent?: string, htmlId: string }> = ({ htmlContent, htmlId }) => {

    return (
        <Tooltip aria-label={'t-' + htmlId} className="rounded w-64 p-4 bg-gray-200" isDisabled={!htmlContent} showArrow={true} content={<div dangerouslySetInnerHTML={{ __html: htmlContent || '' }} />} placement="right" offset={1} >
            <FontAwesomeIcon className={htmlContent ? `cursor-pointer text-msi-midblue-color-500` : `cursor-not-allowed text-gray-200`} icon={faInfoCircle} />
        </Tooltip>
    );

}




interface IDynamiocUIProps {
    sectorFilter: SectorFilter;
    sectorDynamicUIFields: ISectorDynamicUIFields[];
    isFiltersLoading: boolean;
}

export const VesselCharacteristicsDynamicAccordion: React.FC<IDynamiocUIProps> = ({ ...props }) => {

    const [accordionSelection, setAccordionSelection] = useState<string[]>([]);
    const [sectorFilter, setSectorFilter] = useState<SectorFilter>(props.sectorFilter);
    const flagStateList: string[] = useAppSelector(selectFlagStateListSelector);

    const handleAccordionOnClick = (keys: any, selection: string) => {
        
        if (selection === SectorFilterOptions.FILTER && accordionSelection.includes(keys)) {
            setAccordionSelection([]);

        }
        else {
            setAccordionSelection([keys]);
        }

    }

    const updateCheckboxSelection = (value: SectorFilterOptions, sector: Sector, accordionId: string) => {
        sector.setSelection(value);
        if (value === SectorFilterOptions.ALL || value === SectorFilterOptions.NONE) {
            setAccordionSelection([])
        }
        else {
            setAccordionSelection([accordionId]);
        }
    };

    const handleOnClick = (value: SectorFilterOptions) => {

        // const tempCopy = new SectorFilter(JSON.stringify(sectorFilter.sectors))
        props.sectorFilter.sectors.forEach(sector => {            
            sector.setSelection(value);
        });
        setSectorFilter(props.sectorFilter);
        setAccordionSelection([]);
    }

    return (
        <>
            <div className="flex justify-end">
                <AccordionCheckboxGroupLabel handleOnClick={handleOnClick} isFiltersLoading={props.isFiltersLoading} />
            </div>
            <div className="overflow-y-scroll" style={{ scrollbarGutter: 'stable' }}>
                <Accordion
                    className='px-0 pl-2 '
                    isCompact
                    itemClasses={{
                        base: 'rounded',
                        heading: `${accordionSelection.length === 0 ? 'rounded ' : 'rounded-t'} bg-msi-darkblue-color-300 text-msi-white-color h-8 px-2`,
                        title: 'text-msi-darkblue-color-300 text-msi-white-color text-sm',
                        content: 'rounded-b border-msi-darkblue-color-300 border-b border-x',
                    }}
                    hideIndicator
                    showDivider={false}
                    selectedKeys={accordionSelection}
                >
                    {props.sectorDynamicUIFields.map((item) => {
                        const filteredSector = sectorFilter.getSector(item.sectorCode);
                        
                        return (
                            <AccordionItem

                                variant='splitted'
                                textValue={item.name}
                                onPress={() => { filteredSector.setSelection(SectorFilterOptions.FILTER); handleAccordionOnClick(item.htmlId, filteredSector.selection) }}
                                className={`p-0 border`}
                                key={item.htmlId}
                                title={
                                    <div className="flex items-center justify-between space-x-20">
                                        <span>{item.name}</span>
                                        <div className="flex items-center space-x-2">
                                            {filteredSector.selection === SectorFilterOptions.FILTER && isEmpty(filteredSector.filters)
                                                && !accordionSelection.includes(item.htmlId)
                                                && <Chip className='ml-4 flex-none text-[10px] rounded h-5 ' size='sm' color="warning">No filters applied</Chip>}
                                            <AccordionCheckboxGroup key={'cbx-' + item.htmlId} selected={filteredSector.selection} updateCheckboxSelection={(value) => updateCheckboxSelection(value, filteredSector, item.htmlId)} />
                                        </div>
                                    </div>

                                }
                            >

                                {props.isFiltersLoading ? <LoadingSpinner label='Sector filters are loading' /> :
                                    <div className="p-1">
                                        {item.components.map((comp, compIndex) => {
                                            // return <DynamicComponent key={compIndex} data={comp} sector={filteredSector} onChange={(value: string) => handleInputChange("", value)} />

                                            if (comp.type === 'sectorRangeInput') {
                                                return <SectorRangeInput key={compIndex} {...comp.props} sector={filteredSector} />
                                            }
                                            else if (comp.type === 'sectorSelect') {
                                                if (comp.props.name === 'flag') {
                                                    comp.props.options = flagStateList;
                                                }
                                                return <SectorSelect key={compIndex} {...comp.props} sector={filteredSector} />
                                            }
                                            else { return <div>Unknown component: {comp.type}</div>; }

                                        })}
                                    </div>

                                }
                            </AccordionItem>

                        );
                    })}
                </Accordion>
            </div>
        </>
    );
};
